import {
    type IsoAndUnixTimestamp,
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import {
    type EntityState,
    entityStateZodSchema,
} from "@@/shared/entity-status";
import { type PhoneNumber } from "@@/shared/phone";
import { type Price, priceZodSchema } from "@@/shared/prices/price";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";
import { deliveryMethodType } from "../delivery-methods";
import { type ShippingId, shippingIdZodSchema } from "../shipping/shipping-id";
import {
    type HomeDeliveryDestination,
    homeDeliveryDestinationZodSchema,
} from "./home-delivery-destination";
import {
    type HomeDeliveryOrigin,
    homeDeliveryOriginZodSchema,
} from "./home-delivery-origin";
import { type Time, timeZodSchema } from "@@/shared/date-time/time";

type HomeDelivery = {
    readonly _id: string;
    readonly _type: "HOME_DELIVERY";
    readonly _status: EntityState;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly destination: HomeDeliveryDestination;
    readonly origin: HomeDeliveryOrigin;
    readonly estimatedDeliveryTime: IsoAndUnixTimestamp | undefined;
    readonly deliveryMethodType: typeof deliveryMethodType.HOME_DELIVERY;
    readonly shippingId: ShippingId;
    readonly shippingSlot: Time | undefined;
    readonly price: Price;
};

const homeDeliveryFactory = (params: {
    estimatedDeliveryTime: IsoAndUnixTimestamp | undefined;
    destination: HomeDeliveryDestination;
    origin: HomeDeliveryOrigin;
    shippingId: ShippingId;
    shippingSlot?: Time | undefined;
    price: Price;
}): HomeDelivery => {
    return {
        _id: generateId({ prefix: "home_delivery__" }),
        _type: "HOME_DELIVERY",
        _status: "ACTIVE",
        _createdAt: nowIsoAndUnix(),
        deliveryMethodType: deliveryMethodType.HOME_DELIVERY,
        destination: params.destination,
        origin: params.origin,
        estimatedDeliveryTime: params.estimatedDeliveryTime,
        shippingId: params.shippingId,
        shippingSlot: params.shippingSlot,
        price: params.price,
    };
};

// TODO! BIND BELOW TO ZodSchema<HomeDelivery> TYPE WHEN
// TODO! https://github.com/colinhacks/zod/issues/920
// TODO! HAS BEEN ANSWERED OR FIXED
const homeDeliveryZodSchema = z.object({
    _id: z.string(),
    _type: z.literal("HOME_DELIVERY"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    deliveryMethodType: z.literal(deliveryMethodType.HOME_DELIVERY),
    destination: homeDeliveryDestinationZodSchema,
    origin: homeDeliveryOriginZodSchema,
    estimatedDeliveryTime: isoAndUnixZodSchema.or(z.undefined()),
    shippingId: shippingIdZodSchema,
    shippingSlot: timeZodSchema.or(z.undefined()),
    price: priceZodSchema,
}) as z.ZodSchema<HomeDelivery>;

const anonymizeHomeDelivery = (delivery: HomeDelivery): HomeDelivery => {
    return {
        ...delivery,
        destination: {
            ...delivery.destination,
            address: {
                ...delivery.destination.address,
                addressRows: delivery.destination.address.addressRows.map(
                    () => "********",
                ),
                city: "********",
                country: "********",
                zipCode: "********",
                mapUrls: [],
            },
            addressDetails: "********",
            notesToDeliverer: "********",
            recipientName: "********",
            contact: {
                ...delivery.destination.contact,
                email: delivery.destination.contact.email
                    ? {
                          ...delivery.destination.contact.email,
                          emailAddress: "",
                          verified: false,
                      }
                    : undefined,
                phone: {
                    ...delivery.destination.contact.phone,
                    phoneNumber: "" as PhoneNumber,
                    verified: false,
                },
            },
        },
    };
};

export { anonymizeHomeDelivery, homeDeliveryFactory, homeDeliveryZodSchema };
export type { HomeDelivery };
